import React, { useState, useEffect } from "react"
import styled, { css } from "styled-components"
import { Link } from "gatsby"

import arrowIcon from "../../assets/images/arrow-bold.svg"
import useScroll from "../../hooks/use-scroll"

import InvestorsNavbar from "../../components/investors-navbar"
import SEO from "../../components/seo"
import martin from "../../assets/images/martin.png"
import pdfIcon from "../../assets/images/pdf-icon.svg"
import fileIcon from "../../assets/images/file-icon.svg"
import tobias from "../../assets/images/tobias.png"
import alexey from "../../assets/images/alexey2.png"
import emanuel from "../../assets/images/emanuel.png"
import placeholder from "../../assets/images/placeholder.png"
import markus from "../../assets/images/markus.png"
import jack from "../../assets/images/jack.png"
import ebba from "../../assets/images/ebba.png"
import marcus from "../../assets/images/marcus.png"
import arif from "../../assets/images/arif.png"

import {
  H2,
  H4,
  Text,
  Sectionheader,
  PageContainer,
  TextContainer,
} from "../../components/common"

const CustomPageContainer = styled(PageContainer)`
  padding-bottom: 600px;
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 254px 843px 1fr;
  margin-top: 60px;
  width: 100%;

  ${H2} {
    text-align: center;
  }

  @media screen and (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`

const NavigationContainer = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  z-index: 1;
  position: sticky;
  top: 150px;

  @media screen and (max-width: 980px) {
    display: none;
  }
`

const NavigationButton = styled(Link)`
  font-size: 16px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  padding: 0 20px;
  cursor: pointer;
  position: relative;

  &:before {
    content: "";
    position: absolute;
    right: 0;
    height: 100%;
    width: 50px;
    background: url(${arrowIcon}) no-repeat center;
    opacity: 0;
  }

  &:after {
    position: absolute;
    content: "";
    width: 100%;
    height: 100%;
    left: 0;
    background: #fff;
    z-index: -1;
    box-shadow: 4px 4px 0 0 rgba(0, 0, 0, 0.1);
    border: 1px solid #000;
    opacity: 0;
  }

  ${(props) =>
    props.active &&
    css`
      font-weight: 700;

      &:after,
      &:before {
        opacity: 1;
      }
    `}
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-left: 60px;

  a:first-of-type {
    transform: translateY(-160px);
  }

  & > h2 {
    font-size: 18px;
    font-weight: 700;
    text-transform: uppercase;
    color: #535353;
    margin-bottom: 45px;

    ${(props) =>
      props.reverseHeader &&
      css`
        text-align: right;
      `}
  }

  @media screen and (max-width: 980px) {
    padding-left: 0;
  }

  @media screen and (max-width: 640px) {
    margin-bottom: 0;

    & > h2 {
      margin-bottom: 100px;

      ${(props) =>
        props.reverseHeader &&
        css`
          text-align: left;
        `}
    }
  }
`

const PictureBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 200px;
  height: 200px;
  background: #fff;
  position: relative;
  justify-content: center;
  padding: 32px;
  box-sizing: border-box;
  margin-bottom: 126px;
  position: absolute;
  right: 70px;
  top: -100px;
  background: ${(props) => `url(${props.picture})`} no-repeat center/cover;
  border-radius: 50%;

  @media screen and (max-width: 980px) {
    width: 160px;
    height: 160px;
    right: 40px;
    top: -80px;
  }

  @media screen and (max-width: 640px) {
    width: 140px;
    height: 140px;
    top: -70px;
  }
`

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
  border: 1px solid #000000;
  position: relative;
  justify-content: center;
  padding: 32px;
  box-sizing: border-box;
  margin-bottom: 180px;

  div.info-text {
    display: contents;
  }

  &:last-child {
    margin-bottom: 0;
  }

  h3 {
    font-size: 24px;
    line-height: 36px;
    margin-bottom: 6px;
  }

  & > span {
    color: #545454;
    font-size: 16px;
    line-height: 28px;
    margin-bottom: 33px;
    margin-top: 30px;
  }

  p {
    font-size: 16px;
    line-height: 28px;
    margin-bottom: 16px;

    span {
      font-weight: 700;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ${(props) =>
    props.reverse &&
    css`
      h3,
      & > span {
        margin-left: 240px;
      }

      ${PictureBox} {
        right: auto;
      }

      @media screen and (max-width: 980px) {
        h3,
        & > span {
          margin-left: 180px;
        }
      }

      @media screen and (max-width: 640px) {
        h3,
        & > span {
          margin-left: 0;
        }
      }
    `}

  &::after {
    content: "";
    position: absolute;
    border: 1px solid #000000;
    border-left: none;
    transform: skewY(30deg);
    transform-origin: left bottom;
    height: calc(100% + 2px);
    width: 31px;
    top: -1px;
    left: calc(100% + 1px);
    pointer-events: none;
    background: ${(props) => props.color};
    box-sizing: border-box;

    @media screen and (max-width: 640px) {
      width: 15px;
    }
  }

  &::before {
    content: "";
    position: absolute;
    border: 1px solid #000000;
    border-left: 2px solid #000;
    border-top: none;
    transform: skew(60deg);
    transform-origin: right top;
    height: 18px;
    width: calc(100% + 2px);
    top: calc(100% + 1px);
    right: -1px;
    pointer-events: none;
    background: ${(props) => props.color};
    box-sizing: border-box;

    @media screen and (max-width: 640px) {
      height: 9px;
    }
  }

  @media screen and (max-width: 980px) {
    h3 {
      font-size: 24px;
      font-weight: 900;
      margin-bottom: 6px;
    }

    & > span {
      font-size: 16px;
      line-height: 28px;
      margin-bottom: 24px;
    }
  }

  @media screen and (max-width: 640px) {
    padding: 90px 20px 32px;
    margin-bottom: 24px;

    h3 {
      font-size: 24px;
      font-weight: 900;
    }

    div.info-text {
      display: none;
    }

    ${PictureBox} {
      right: auto;
      left: 20px;
    }

    & > span {
      margin-bottom: 0;
    }

    ${(props) =>
      props.padded &&
      css`
        div.info-text {
          display: flex;
        }
      `}
  }
`

const InfoTextMobile = styled.div`
  margin-bottom: 120px;
  display: none;

  @media screen and (max-width: 640px) {
    display: block;
  }

  h3 {
    font-size: 24px;
    line-height: 36px;
    margin-bottom: 6px;
  }

  & > span {
    color: #545454;
    font-size: 16px;
    line-height: 28px;
    margin-bottom: 33px;
  }

  p {
    font-size: 16px;
    line-height: 28px;
    margin-bottom: 16px;

    span {
      font-weight: 700;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const AuditorSummary = styled.p`
  line-height: 28px;
  margin-top: 14px;

  @media screen and (max-width: 640px) {
    width: auto;
    br {
      display: block;
    }
  }
`

const seniorExecutives = [
  {
    name: "Arif Rehman",
    position: "Chief Executive Officer (CEO) since 2026.",
    born: "1978",
    education:
      "Master of Science Electrical Engineering, Chalmers University of Technology. Over 20 years of experience from leading roles in Nordic tech and SaaS companies, with a focus on product innovation, commercial scalability and building strong teams in both scaling and transition phases and has held executive roles such as CEO and CPO in both privately owned and listed companies.",
    otherAssignments: "President and owner of Evolvien AB.",
    holdings: "0 shares and 490,000 stock options",
    color: "#D098D7",
    picture: arif,
  },
  {
    name: "Martin Bäuml",
    position: "Chief Financial Officer (CFO) since 2020.",
    born: "1980",
    education:
      "Master of Business Administration, Stockholm School of Economics (2005). Previously worked in investment banking at Credit Suisse in London (2007–2010), Citi in Stockholm (2010–2015) and Handelsbanken (2015–2018).",
    otherAssignments: "President and owner of Mabaco LLC.",
    holdings: "199,616 shares and 72,499 warrants.",
    color: "#D098D7",
    picture: martin,
    reverse: true,
  },
  {
    name: "Markus Terland",
    position: "COO Since 2017",
    born: "1986",
    education:
      "Bachelor of Economics, Lunds University. Previous experience Risk & Product at Klarna",
    otherAssignments: "-",
    holdings: "716,927 shares and 105,000 stock options",
    color: "#D098D7",
    picture: markus,
  },
  {
    name: "Jack Brånfelt",
    position: "Chief Technology Officer since 2025",
    born: "1991",
    education:
      "Previous experience at Klarna: first within Merchant Support (2013-2016) and then Engineering (2016-2018). Joined Checkin.com in 2018 as Head of Integration, then as Head of TechOps, before taking over as CTO in 2025.",
    otherAssignments: "-",
    holdings: "5,569 shares and 105,000 stock options",
    color: "#D098D7",
    picture: jack,
    reverse: true,
  },
  {
    name: "Ebba Sidhagen Nilsson",
    position: "Chief Product Officer (CPO) since 2022",
    born: "1986",
    education:
      "Master of Business Administration, Uppsala University (2011). Previous experience in the technology sector, including roles at Salesforce (2015–2019), The Football Association (2014–2015) in London, and Releye (2011–2014) in Stockholm.",
    otherAssignments: "-",
    holdings: "100,884 shares and 52,500 stock options",
    color: "#D098D7",
    picture: ebba,
  },
  {
    name: "Marcus Bengtsson",
    position: "Chief Marketing Officer (CMO) since 2019",
    born: "1989",
    education:
      "Chief Marketing Officer at Checkin.com since 2019. Previous experience: founder and CEO of a digital marketing agency, and earlier led teams in a contracted role supporting Google’s advertising and sales operations.",
    otherAssignments: "-",
    holdings: "0 shares and 70,000 stock options",
    color: "#D098D7",
    picture: marcus,
    reverse: true,
  },
]

const boardOfdirectors = [
  {
    name: "Emanuel Stihl",
    position: "Board member since 2025. Chairman of the board since 2026.",
    born: "1985",
    education:
      "Master of Science in Technology Management from Lund University. CIO at Fårö Capital. Founder and CEO of Venture Capital firm Fåhraeus Startup & Growth and partner in FSG Fund II AB.",
    otherAssignments: "Board member of Eletive AB.",
    holdings: "620,000 shares",
    dependency:
      "Dependent in relation to the company's major shareholders. Independent in relation to the Company and the company management.",
    color: "#C0EAD6",
    picture: emanuel,
  },
  {
    name: "Alexey Kuznetsov",
    position: "Board member since 2025.",
    born: "1983",
    education:
      "Master's degree in Quality Management from the Saint Petersburg Electrotechnical University. Previous experience from Lead Product Designer at Klarna Bank (2014–2016). Alexey Kuznetsov is also a co-founder of and former employee of Checkin.com Group AB.",
    otherAssignments:
      "Principal owner of Alexey Kuznetsov AB and Akapa Vision SL, through which Alexey performs consulting services for the Company.",
    holdings: "2,880,759 shares through Alexey Kuznetsov AB.",
    dependency:
      "Independent in relation to the company's major shareholders. Dependent in relation to the Company and the company management.",
    color: "#C0EAD6",
    picture: alexey,
    reverse: true,
  },
  {
    name: "Tobias Lindh",
    position: "Board member since 2025.",
    born: "1972",
    education:
      "Managing Director for Adyen in the Nordics & Baltics since 2012. Previous experience as Senior Business Development Manager - EMEA at Worldpay (2010-2012), Senior Sales Manager - Nordic Region at The Royal Bank of Scotland (2007-2010) and various roles within the Kinnevik Group (1997-2007).",
    otherAssignments: "Member of the board of Adyen Nordic Bank Filial.",
    holdings: "0",
    dependency:
      "Independent in relation to the Company's major shareholders as well as the Company and the company management.",
    color: "#C0EAD6",
    picture: tobias,
  },
]

const Navigation = ({ index = 0, onNavigate = () => {} }) => {
  return (
    <NavigationContainer>
      <NavigationButton
        to="#directors"
        active={index === 0}
        onClick={() => onNavigate(0)}
      >
        Board of directors
      </NavigationButton>
      <NavigationButton
        to="#executives"
        active={index === 1}
        onClick={() => onNavigate(1)}
      >
        Senior executives
      </NavigationButton>
      <NavigationButton
        to="#auditor"
        active={index === 2}
        onClick={() => onNavigate(2)}
      >
        Auditor
      </NavigationButton>
      <NavigationButton
        to="#adviser"
        active={index === 3}
        onClick={() => onNavigate(3)}
      >
        Certified Adviser
      </NavigationButton>
      <NavigationButton
        to="#general-meetings"
        active={index === 4}
        onClick={() => onNavigate(4)}
      >
        General meetings
      </NavigationButton>
      <NavigationButton
        to="#annual-general-meeting-2026"
        active={index === 5}
        onClick={() => onNavigate(5)}
      >
        AGM 2026
      </NavigationButton>
      <NavigationButton
        to="#annual-general-meeting"
        active={index === 6}
        onClick={() => onNavigate(6)}
      >
        AGM 2025
      </NavigationButton>
      <NavigationButton
        to="#extra-general-meeting"
        active={index === 7}
        onClick={() => onNavigate(7)}
      >
        EGM 2025
      </NavigationButton>
      <NavigationButton
        to="#articles-of-association"
        active={index === 8}
        onClick={() => onNavigate(8)}
      >
        Articles of association
      </NavigationButton>
      <NavigationButton
        to="#nomination_committee"
        active={index === 9}
        onClick={() => onNavigate(9)}
      >
        Nomination Committee
      </NavigationButton>
    </NavigationContainer>
  )
}

const InfoBoxComponent = ({ data, children, padded }) => {
  return (
    <>
      <InfoBox color={data.color} reverse={data.reverse} padded={padded}>
        <PictureBox picture={data.picture} />
        {data.name && <h3>{data.name}</h3>}
        {data.position && <span>{data.position}</span>}
        <div className="info-text">
          {data.born && (
            <p>
              <span>Born: </span>
              {data.born}
            </p>
          )}
          {data.education && (
            <p>
              <span>Education and Experience: </span>
              {data.education}
            </p>
          )}
          {data.otherAssignments && (
            <p>
              <span>Other assignments: </span>
              {data.otherAssignments}
            </p>
          )}
          {data.holdings && (
            <p>
              <span>Holdings in the Company: </span>
              {data.holdings}
            </p>
          )}
          {data.dependency && (
            <p>
              <span>Position of dependency: </span>
              {data.dependency}
            </p>
          )}
          {children}
        </div>
      </InfoBox>

      <InfoTextMobile>
        {data.born && (
          <p>
            <span>Born: </span>
            {data.born}
          </p>
        )}
        {data.education && (
          <p>
            <span>Education and Experience: </span>
            {data.education}
          </p>
        )}
        {data.otherAssignments && (
          <p>
            <span>Other assignments: </span>
            {data.otherAssignments}
          </p>
        )}
        {data.holdings && (
          <p>
            <span>Holdings in the Company: </span>
            {data.holdings}
          </p>
        )}
        {data.dependency && (
          <p>
            <span>Position of dependency: </span>
            {data.dependency}
          </p>
        )}
        {/* {children} */}
      </InfoTextMobile>
    </>
  )
}

const Table = styled.table`
  border-spacing: 0px;
  border-collapse: collapse;

  td {
    border: 1px solid #afafaf;
    border-left: none;
    border-right: none;

    & > div {
      display: flex;
      height: 80px;
      align-items: center;
      justify-content: space-between;
      padding-left: 20px;
    }

    &:last-of-type > div {
      @media screen and (max-width: 640px) {
        height: 110px;
      }
    }
    span {
      font-size: 20px;
      font-weight: 700;
      display: block;
      margin-bottom: 0px;
      max-width: 550px;

      @media screen and (max-width: 640px) {
        font-size: 16px;
        width: 196px;
        line-height: 24px;
      }
    }

    a {
      font-size: 14px;
      color: #0070c9;
      text-decoration: underline;

      @media screen and (max-width: 980px) {
        font-size: 13px;
      }
    }
  }
`

const Header = styled(H2)`
  @media screen and (max-width: 980px) {
    font-size: 40px;
  }

  @media screen and (max-width: 640px) {
    font-size: 34px;
  }
`

const FileButton = styled.button`
  width: 250px;
  height: 100%;
  box-shadow: none;
  border: none;
  background: url(${fileIcon}) no-repeat center;
  outline: none;
  cursor: pointer;

  @media screen and (max-width: 980px) {
    width: 50px;
  }
`

const PDFButton = styled(FileButton)`
  background: url(${pdfIcon}) no-repeat center;
`

const AuditorContentContainer = styled(ContentContainer)`
  @media screen and (max-width: 640px) {
    ${InfoBox} {
      padding: 32px 20px;
    }

    h2 {
      margin-bottom: 40px;
    }
  }
`

export default function GovernancePage() {
  const [navigationIndex, setNavigationIndex] = useState(0)
  const [disableScrollTracking, setDisableScrollTracking] = useState(false)
  let scroll = useScroll()

  const debug = false
  const secionSizes = [2300, 3690, 437, 400, 1040, 770, 820, 390, 310, 1000]

  const cumulativeSectionSizes = secionSizes.reduce((acc, size) => {
    const lastValue = acc.length > 0 ? acc[acc.length - 1] : 0
    acc.push(lastValue + size)
    return acc
  }, [])

  const findCurrentSectionIndex = (scroll) => {
    for (let i = 0; i < cumulativeSectionSizes.length; i++) {
      if (scroll < cumulativeSectionSizes[i]) {
        return i
      }
    }
    return cumulativeSectionSizes.length - 1
  }

  useEffect(() => {
    if (disableScrollTracking) return
    if (debug) {
      const sIdx = findCurrentSectionIndex(scroll)
      console.log(
        sIdx,
        "ps:",
        secionSizes[sIdx],
        "cx:",
        secionSizes[sIdx] - (cumulativeSectionSizes[sIdx] - scroll)
      )
    }
    setNavigationIndex(findCurrentSectionIndex(scroll))
  }, [scroll, disableScrollTracking])

  const onNavigate = (index) => {
    setDisableScrollTracking(true)
    setNavigationIndex(index)
    setTimeout(() => {
      setDisableScrollTracking(false)
    }, 1500)
  }

  return (
    <>
      <SEO
        title="Checkin.com Group AB - Governance"
        description="Find information related to governance, board of directors, auditor, general meetings, and more."
        canonical="/investors/governance"
        ogImage="/images/og.jpg"
      />
      <CustomPageContainer>
        <InvestorsNavbar />
        <Sectionheader>
          <Header>Governance</Header>
        </Sectionheader>

        <GridContainer>
          <di>
            <Navigation index={navigationIndex} onNavigate={onNavigate} />
          </di>

          <div>
            <ContentContainer>
              <a id="directors"></a>
              <h2>Board of directors</h2>
              {boardOfdirectors.map((data, index) => (
                <InfoBoxComponent key={index} data={data} />
              ))}
            </ContentContainer>

            <ContentContainer>
              <a id="executives"></a>
              <h2>Senior executives</h2>
              {seniorExecutives.map((data, index) => (
                <InfoBoxComponent key={index} data={data} />
              ))}
            </ContentContainer>

            <AuditorContentContainer>
              <a id="auditor"></a>
              <h2>Auditor</h2>
              <InfoBoxComponent
                padded
                data={{
                  name: "Ludvig Kollberg",
                  color: "#B4DEEC",
                }}
              >
                <AuditorSummary>
                  The auditor of Checkin.com Group is MOORE Sweden, with Ludvig
                  Kollberg as auditor-in-charge since the 2017 annual general
                  meeting. Ludvig Kollberg is an authorized public accountant
                  and member of FAR.
                </AuditorSummary>
              </InfoBoxComponent>
            </AuditorContentContainer>

            <AuditorContentContainer>
              <a id="adviser"></a>
              <h2>Certified Adviser</h2>
              <InfoBoxComponent
                padded
                data={{
                  name: "DNB Carnegie Investment Bank AB (publ)",
                  color: "#B4DEEC",
                }}
              >
                <AuditorSummary>
                  <span style={{ whiteSpace: "nowrap" }}>
                    {" "}
                    tel +46 (0)8 588 68 570
                  </span>
                  <br />
                  certifiedadviser@dnbcarnegie.se
                </AuditorSummary>
              </InfoBoxComponent>
            </AuditorContentContainer>

            <TextContainer>
              <a id="general-meetings"></a>
              <H2>General meetings</H2>

              <H4>General shareholder meeting</H4>
              <Text>
                Pursuant to the Swedish Companies Act, the general shareholder
                meeting is the Company's highest decision-making body. At the
                general meeting, the shareholders exercise their voting rights
                on key issues, such as the adoption of income statements and
                balance sheets, appropriation of the Company's profit, discharge
                from liability of board members and the CEO, election of board
                members and auditors and remuneration of the board and auditors.
                <br />
                <br />
                In addition to the Annual General Meeting (AGM), Extraordinary
                General Meetings (EGMs) may be held. In accordance with
                Checkin.com Group AB's articles of association, notice to Annual
                General Meetings and Extraordinary General Meetings is made by
                announcement in the Official Swedish Gazette (Sw. Post och
                Inrikes Tidningar) and by making the notice available on
                Checkin.com Group's website. An announcement that notice has
                been issued is also published in Dagens Industri.
              </Text>

              <H4>Right to attend general shareholder meetings</H4>
              <Text>
                All shareholders who are directly registered in the share
                register maintained by Euroclear six weekdays before the general
                shareholder meeting and have notified the Company of their
                intention to participate (with potential assistants) in the
                general meeting not later than the date stated in the notice
                convening the general meeting have the right to attend the
                general meeting and vote for the number of shares they hold.
                Shareholders may attend the general shareholder meeting in
                person or by proxy and may also be accompanied by a maximum of
                two assistants. Shareholders can normally register for the
                general shareholder meeting in several different ways stated in
                the convening notice for the meeting.
              </Text>

              <H4>Shareholder initiatives</H4>
              <Text>
                Shareholders who wish to have a matter addressed at the general
                shareholder meeting must submit a written request to the Board
                of Directors. The request must normally have been received by
                the board not later than seven weeks before the general
                shareholder meeting.
              </Text>
            </TextContainer>
            <TextContainer>
              <a id="annual-general-meeting-2026"></a>
              <H2>Annual General Meeting 2026</H2>

              <Table>
                <tbody>
                  <tr>
                    <td>
                      <div>
                        <span>Form of power of attorney (Eng)</span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2026/Form_of_power_of_attorney_Eng.docx",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>Form of power of attorney (Swe)</span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2026/Form_of_power_of_attorney_Swe.docx",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>
                          Notice of the Annual General Meeting 2026 (Swe)
                        </span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2026/Notice_of_the_Annual_General_Meeting_2026_SWE.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>
                          The Nomination Committee's motivated opinion (Swe)
                        </span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2026/The_Nomination_Committee_s_motivated_opinion_2026_SWE.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>
                          Proposals for resolutions at the Annual General
                          Meeting (Swe)
                        </span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2026/Proposals_for_resolutions_at_the_Annual_General_Meeting_Swe.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>
                          Information Regarding Suggested New Members of the
                          Board
                        </span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2026/Information_Regarding_Suggested_New_Members_of_the_Board.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>
                          Minutes of the Checkin.com Annual General Meeting 2026
                          (Swe)
                        </span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2026/Checkin_com_Group_AB_Stammoprotokoll_2026.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </TextContainer>
            <TextContainer>
              <a id="annual-general-meeting"></a>
              <H2>Annual General Meeting 2025</H2>

              <Table>
                <tbody>
                  <tr>
                    <td>
                      <div>
                        <span>Form of power of attorney (Eng)</span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2023/Form_of_power_of_attorney_Eng.docx",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>Form of power of attorney (Swe)</span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2023/Form_of_power_of_attorney_Swe.docx",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>Notice of the Annual General Meeting</span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2025/Notice_of_the_Annual_General_Meeting_2025.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>
                          The Nomination Committee´s motivated opinion (Swe)
                        </span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2025/The_Nomination_Committee_s_motivated_opinion_2025.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>
                          Proposals for resolutions at the Annual General
                          Meeting (Swe)
                        </span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2025/Proposals_for_Resolutions_at_the_AGM.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <div>
                        <span>
                          Information Regarding Suggested New Members of the
                          Board
                        </span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2025/Information_Regarding_Suggested_New_Members_of_the_Board.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <div>
                        <span>
                          Minutes of the Checkin.com Annual General Meeting 2025
                        </span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2025/Checkin_com_Group_AB_Protokoll_arsstamma.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </TextContainer>

            <TextContainer>
              <a id="extra-general-meeting"></a>
              <H2>Extra General Meeting 2025</H2>

              <Table>
                <tbody>
                  <tr>
                    <td>
                      <div>
                        <span>Notice of the Extra General Meeting 2025</span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2025/Kallelse-till-extra-bolagsstamma-i-checkin-com-group-ab-publ.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div>
                        <span>Minutes of the Extra General Meeting 2025</span>
                        <FileButton
                          onClick={() =>
                            window.open(
                              "/documents/2025/Checkin_com_Group_AB_Protokoll_extrastamma_oktober_2025_Rostlangd_avsiktligt_borttagen.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </TextContainer>

            <TextContainer>
              <a id="articles-of-association"></a>
              <H2>Articles of association</H2>

              <Table>
                <tbody>
                  <tr>
                    <td>
                      <div>
                        <span>Bolagsordning - 559096-3087</span>
                        <PDFButton
                          onClick={() =>
                            window.open(
                              "/documents/Bolagsordning_-_559096-3087.pdf",
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </TextContainer>

            <TextContainer>
              <a id="nomination_committee"></a>
              <H2>
                Nomination Committee in Checkin.com Group AB (publ) ahead of
                Annual General Meeting 2027
              </H2>

              <Text>
                In accordance with the decision taken by Checkin.com Group AB
                (publ)'s Annual General Meeting in 2021, the three largest
                shareholders in the company as of 30 September each year have
                the right to each appoint a member of the Nomination Committee
                ahead of the Annual General Meeting the following year.
              </Text>
              <Text>
                Shareholders who wish to contact the Nomination Committee may do
                so in writing to:
              </Text>
              <Text>
                Checkin.com Group AB
                <br />
                Att: Nomination Committee
                <br />
                Grev Turegatan 30
                <br />
                114 38 Stockholm
              </Text>
            </TextContainer>
          </div>
        </GridContainer>
      </CustomPageContainer>
    </>
  )
}
